/**
 * Context Orchestrator Service
 *
 * Aggregates all relevant context for AI chat responses. Used in two ways:
 *
 * 1. Without RAG (explicit only): getContextForThread / getContextForNewThread
 *    are called directly from useChatSend when RAG is off or no query. All
 *    sources below are included in allSources.
 *
 * 2. With RAG: contextOrchestratorRAG.getEnhancedContext / getEnhancedContextForNewThread
 *    call these methods first to get explicit sources, then add semantic retrieval
 *    from ragService.search (embedded docs). Explicit sources (including space/topic
 *    docs) are always included; semantic sources add relevant chunks when docs are
 *    embedded.
 *
 * Context includes:
 * - Space-level sources (sources table) and space documents (knowledge base)
 * - Topic-level documents (Knowledge Base rows linked via metadata/columns, same as Topic UI)
 * - Thread-attached documents
 * - User profile (when personalization is enabled)
 * - Agent persona, memory, and capabilities
 * - Super prompts and workflow context
 *
 * Returns a unified ContextPayload that can be injected into AI prompts.
 */

import { supabase, getCurrentUserId } from "@/lib/supabase";
import { sourcesService } from "./sourcesService";
import { knowledgeBaseService, type KnowledgeBaseItem } from "./knowledgeBaseService";
import { chatThreadsService } from "./chatThreadsService";
import { userProfileService, generateResponseInstructions } from "./userProfileService";
import { normalizeSourceOfTruthDocIds, isSourceOfTruthEnabledFromPrefs } from "@/lib/sourceOfTruthPreferences";
import type { ResponsePreferences } from "./userProfileService";
import type { ChatThread } from "@shared/schema";
import { AGENTS, QUICK_PROMPTS, getAgentById as getBuiltinAgentById } from "@/data/aiToolkit";
import { isWorkflowUuid } from "@/services/aiWorkflowsService";
import { asString } from "@/lib/chatHistoryLimits";

export interface SourceContext {
  id: string;
  title: string;
  content: string;
  type: string;
  url?: string | null;
  citationIndex: number;
  /** Mastermind space this source belongs to (resolved from RAG chunk metadata) */
  spaceName?: string;
  /** Topic this source belongs to (resolved from RAG chunk metadata) */
  topicName?: string;
}

export interface TopicDocumentManifestEntry {
  id: string;
  title: string;
  index: number;
}

/** Title-only inventory of chat threads in the active space/topic Knowledge scope. */
export interface ThreadManifestEntry {
  id: string;
  title: string;
  index: number;
}

export type SourceOfTruthManifestEntry = TopicDocumentManifestEntry;

export interface UserProfileContext {
  writingStyle?: string | null;
  expertise?: string[];
  communicationPreferences?: string | null;
  worldview?: string | null;
  goals?: string[];
  background?: string | null;
  /** Resume/CV body for personalization (same source as chat send block; included once in USER PROFILE). */
  resumeText?: string | null;
  tone?: string | null;
  verbosity?: string | null;
  learningStyle?: string | null;
  responseFormat?: string | null;
  favoritePhrases?: string[];
  avoidPhrases?: string[];
  referenceAuthors?: string[];
  responsePreferences?: ResponsePreferences | null;
}

export interface AgentContext {
  id: string;
  name: string;
  description?: string;
  specialty?: string;
  lens?: string;
  expertFocus?: string;
  outputRequirements?: {
    format?: string;
    instructions?: string;
    requiredSections?: string[];
    maxLengthHint?: string;
  };
  persona: {
    role?: string;
    expertise?: string[];
    communicationStyle?: string;
    systemPrompt: string;
  } | string;
  capabilityModules?: string[];
  memoryPolicy?: {
    type: 'none' | 'session' | 'persistent';
    retentionDays?: number;
  };
  preferredModel?: string;
}

export interface SuperPromptContext {
  id: string;
  name: string;
  promptTemplate: string;
  filledVariables: Record<string, string>;
}

export interface WorkflowContext {
  id: string;
  name: string;
  currentNodeId: string;
  variables: Record<string, unknown>;
}

export interface ContextPayload {
  threadId: string;
  spaceId?: string | null;
  topicId?: string | null;
  
  spaceSources: SourceContext[];
  topicDocuments: SourceContext[];
  threadAttachments: SourceContext[];
  linkedDocuments: SourceContext[];
  
  userProfile?: UserProfileContext | null;
  personalizationEnabled: boolean;
  
  agent?: AgentContext | null;
  superPrompt?: SuperPromptContext | null;
  workflowContext?: WorkflowContext | null;
  topicDocumentManifest: TopicDocumentManifestEntry[];
  /** Titles of threads in Knowledge scope (space or topic); bodies loaded via RAG expand. */
  threadManifest: ThreadManifestEntry[];
  sourceOfTruthDocuments: SourceContext[];
  sourceOfTruthManifest: SourceOfTruthManifestEntry[];
  attachedDocuments: SourceContext[];

  allSources: SourceContext[];
  citationMap: Map<number, SourceContext>;
  
  metadata: {
    totalSources: number;
    totalTokensEstimate: number;
    /** Source tokens before the pack budget. Used to measure rag_pack savings. */
    uncappedTokensEstimate: number;
    truncated: boolean;
    topicDocumentManifestCount: number;
    topicDocumentContentIncludedCount: number;
    threadManifestCount: number;
    sourceOfTruthManifestCount: number;
    sourceOfTruthContentIncludedCount: number;
    spaceName?: string;
    topicName?: string;
  };
}

const MAX_DOCUMENT_CONTENT_TOKENS = 8000;
const CHARS_PER_TOKEN = 4;
const SSOT_TOKEN_BUDGET_MIN = 1500;
const SSOT_TOKEN_BUDGET_RATIO = 0.25;

export interface ContextOrchestratorOptions {
  personalizationEnabled?: boolean;
  agentId?: string | null;
  superPromptId?: string | null;
  superPromptVariables?: Record<string, string>;
  workflowId?: string | null;
  workflowNodeId?: string | null;
  workflowVariables?: Record<string, unknown>;
  maxTokens?: number;
  selectedDocumentIds?: string[];
  sourceOfTruthEnabled?: boolean;
  /** UI Knowledge scope override (Chat Tools). When set, replaces thread-linked space/topic. */
  spaceId?: string | null;
  topicId?: string | null;
}

function estimateTokens(text: string): number {
  return Math.ceil(text.length / CHARS_PER_TOKEN);
}

function truncateContent(content: string, maxTokens: number): string {
  const maxChars = maxTokens * CHARS_PER_TOKEN;
  if (content.length <= maxChars) return content;
  return content.substring(0, maxChars) + "...[truncated]";
}

function ssotTokenBudget(maxTokens: number): number {
  return Math.max(SSOT_TOKEN_BUDGET_MIN, Math.floor(maxTokens * SSOT_TOKEN_BUDGET_RATIO));
}

interface AssembledSourcesResult {
  allSources: SourceContext[];
  citationMap: Map<number, SourceContext>;
  totalTokensEstimate: number;
  uncappedTokensEstimate: number;
  truncated: boolean;
  topicDocumentContentIncludedCount: number;
  sourceOfTruthContentIncludedCount: number;
}

class ContextOrchestratorService {
  async getContextForThread(
    threadId: string,
    options: ContextOrchestratorOptions = {}
  ): Promise<ContextPayload> {
    const {
      personalizationEnabled = true,
      agentId = null,
      superPromptId = null,
      superPromptVariables = {},
      workflowId = null,
      workflowNodeId = null,
      workflowVariables = {},
      maxTokens = MAX_DOCUMENT_CONTENT_TOKENS,
      selectedDocumentIds = [],
      sourceOfTruthEnabled = true,
    } = options;

    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error("User not authenticated");
    }

    const thread = await this.getThread(threadId);
    if (!thread) {
      throw new Error("Thread not found");
    }

    // Knowledge scope from Chat Tools overrides thread-linked space/topic when provided
    const spaceId =
      options.spaceId !== undefined ? options.spaceId : thread.linkedSpaceIds?.[0] || null;
    const topicId =
      options.topicId !== undefined ? options.topicId : thread.topicId || null;

    const [
      sourceOfTruthItems,
      attachedItems,
      spaceSources,
      spaceKnowledgeDocuments,
      topicKnowledgeItems,
      threadAttachments,
      linkedDocuments,
      userProfile,
      spaceDetails,
      topicDetails,
      agentTrainingSources,
      threadManifestRaw,
    ] = await Promise.all([
      sourceOfTruthEnabled ? this.getSourceOfTruthKnowledgeItems() : Promise.resolve([] as KnowledgeBaseItem[]),
      this.getKnowledgeItemsByIds(selectedDocumentIds),
      spaceId ? this.getSpaceSources(spaceId) : Promise.resolve([]),
      spaceId ? this.getSpaceKnowledgeDocuments(spaceId, topicId) : Promise.resolve([]),
      topicId && spaceId
        ? knowledgeBaseService.getKnowledgeBaseItemsForTopic(spaceId, topicId)
        : Promise.resolve([] as KnowledgeBaseItem[]),
      this.getThreadAttachments(threadId),
      this.getLinkedDocuments(threadId),
      personalizationEnabled ? this.getUserProfile() : Promise.resolve(null),
      spaceId ? this.getSpaceDetails(spaceId) : Promise.resolve(null),
      topicId ? this.getTopicDetails(topicId) : Promise.resolve(null),
      agentId ? this.getAgentTrainingSources(agentId) : Promise.resolve([] as SourceContext[]),
      this.getThreadManifestForScope(spaceId, topicId),
    ]);

    if (topicId && !spaceId) {
      console.warn(
        "[contextOrchestrator] Thread has topicId but no linked space; cannot load topic Knowledge Base documents for manifest/content."
      );
    }
    if (topicId && spaceId && topicKnowledgeItems.length === 0) {
      console.warn(
        `[contextOrchestrator] Knowledge Base returned 0 documents for topic ${topicId} in space ${spaceId}. ` +
          "If the Topic UI lists documents here, verify knowledgebase metadata.topicIds/topicId and space access."
      );
    }

    const topicDocumentManifest: TopicDocumentManifestEntry[] = topicId
      ? topicKnowledgeItems.map((item, index) => ({
          id: asString(item.id),
          title: item.title || "Untitled",
          index: index + 1,
        }))
      : spaceKnowledgeDocuments.map((item, index) => ({
          id: asString(item.id),
          title: item.title || "Untitled",
          index: index + 1,
        }));
    const threadManifest = threadManifestRaw;
    const topicDocuments = this.mapKnowledgeBaseItemsToTopicSources(topicKnowledgeItems);
    const sourceOfTruthDocuments = this.mapKnowledgeBaseItemsToTopicSources(sourceOfTruthItems);
    const sourceOfTruthManifest: SourceOfTruthManifestEntry[] = sourceOfTruthItems.map((item, index) => ({
      id: asString(item.id),
      title: item.title || "Untitled",
      index: index + 1,
    }));
    const attachedDocuments = this.mapKnowledgeBaseItemsToTopicSources(attachedItems);

    const agent = agentId ? await this.getAgent(agentId) : null;
    const superPrompt = superPromptId ? this.getSuperPrompt(superPromptId, superPromptVariables) : null;
    const workflowContext = workflowId && workflowNodeId ? {
      id: asString(workflowId),
      name: "Workflow",
      currentNodeId: workflowNodeId,
      variables: workflowVariables
    } : null;

    const ssotIds = new Set(sourceOfTruthDocuments.map((s) => s.id));
    const attachedIds = new Set(attachedDocuments.map((s) => s.id));
    const spaceAndTopicIds = new Set([...spaceSources, ...topicDocuments].map((s) => s.id));
    const topicMetaSources = topicId ? this.topicSelfSource(topicId, topicDetails) : [];

    const assembled = this.assembleExplicitSources({
      maxTokens,
      sourceOfTruthDocuments,
      sourceOfTruthManifest,
      topicDocumentManifest,
      layers: [
        topicMetaSources,
        attachedDocuments.filter((d) => !ssotIds.has(d.id)),
        spaceSources.filter((d) => !ssotIds.has(d.id) && !attachedIds.has(d.id)),
        topicDocuments.filter((d) => !ssotIds.has(d.id) && !attachedIds.has(d.id)),
        spaceKnowledgeDocuments.filter(
          (d) => !ssotIds.has(d.id) && !attachedIds.has(d.id) && !spaceAndTopicIds.has(d.id)
        ),
        threadAttachments.filter((d) => !ssotIds.has(d.id) && !attachedIds.has(d.id)),
        linkedDocuments.filter((d) => !ssotIds.has(d.id) && !attachedIds.has(d.id)),
        agentTrainingSources.filter((d) => !ssotIds.has(d.id) && !attachedIds.has(d.id)),
      ],
    });

    return {
      threadId,
      spaceId,
      topicId,
      spaceSources,
      topicDocuments,
      threadAttachments,
      linkedDocuments,
      sourceOfTruthDocuments,
      sourceOfTruthManifest,
      attachedDocuments,
      userProfile: personalizationEnabled ? userProfile : null,
      personalizationEnabled,
      agent,
      superPrompt,
      workflowContext,
      topicDocumentManifest,
      threadManifest,
      allSources: assembled.allSources,
      citationMap: assembled.citationMap,
      metadata: {
        totalSources: assembled.allSources.length,
        totalTokensEstimate: assembled.totalTokensEstimate,
        uncappedTokensEstimate: assembled.uncappedTokensEstimate,
        truncated: assembled.truncated,
        topicDocumentManifestCount: topicDocumentManifest.length,
        topicDocumentContentIncludedCount: assembled.topicDocumentContentIncludedCount,
        threadManifestCount: threadManifest.length,
        sourceOfTruthManifestCount: sourceOfTruthManifest.length,
        sourceOfTruthContentIncludedCount: assembled.sourceOfTruthContentIncludedCount,
        spaceName: spaceDetails?.name,
        topicName: topicDetails?.name
      }
    };
  }

  async getContextForNewThread(
    options: ContextOrchestratorOptions & {
      spaceId?: string | null;
      topicId?: string | null;
    } = {}
  ): Promise<Omit<ContextPayload, 'threadId'> & { threadId: null }> {
    const {
      spaceId = null,
      topicId = null,
      personalizationEnabled = true,
      agentId = null,
      superPromptId = null,
      superPromptVariables = {},
      maxTokens = MAX_DOCUMENT_CONTENT_TOKENS,
      selectedDocumentIds = [],
      sourceOfTruthEnabled = true,
    } = options;

    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error("User not authenticated");
    }

    const [
      sourceOfTruthItems,
      attachedItems,
      spaceSources,
      spaceKnowledgeDocuments,
      topicKnowledgeItems,
      userProfile,
      spaceDetails,
      topicDetails,
      agentTrainingSources,
      threadManifestRaw,
    ] = await Promise.all([
      sourceOfTruthEnabled ? this.getSourceOfTruthKnowledgeItems() : Promise.resolve([] as KnowledgeBaseItem[]),
      this.getKnowledgeItemsByIds(selectedDocumentIds),
      spaceId ? this.getSpaceSources(spaceId) : Promise.resolve([]),
      spaceId ? this.getSpaceKnowledgeDocuments(spaceId, topicId) : Promise.resolve([]),
      topicId && spaceId
        ? knowledgeBaseService.getKnowledgeBaseItemsForTopic(spaceId, topicId)
        : Promise.resolve([] as KnowledgeBaseItem[]),
      personalizationEnabled ? this.getUserProfile() : Promise.resolve(null),
      spaceId ? this.getSpaceDetails(spaceId) : Promise.resolve(null),
      topicId ? this.getTopicDetails(topicId) : Promise.resolve(null),
      agentId ? this.getAgentTrainingSources(agentId) : Promise.resolve([] as SourceContext[]),
      this.getThreadManifestForScope(spaceId, topicId),
    ]);

    if (topicId && !spaceId) {
      console.warn(
        "[contextOrchestrator] New thread has topicId but no spaceId; cannot load topic Knowledge Base documents for manifest/content."
      );
    }
    if (topicId && spaceId && topicKnowledgeItems.length === 0) {
      console.warn(
        `[contextOrchestrator] Knowledge Base returned 0 documents for topic ${topicId} in space ${spaceId}. ` +
          "If the Topic UI lists documents here, verify knowledgebase metadata.topicIds/topicId and space access."
      );
    }

    const topicDocumentManifest: TopicDocumentManifestEntry[] = topicId
      ? topicKnowledgeItems.map((item, index) => ({
          id: asString(item.id),
          title: item.title || "Untitled",
          index: index + 1,
        }))
      : spaceKnowledgeDocuments.map((item, index) => ({
          id: asString(item.id),
          title: item.title || "Untitled",
          index: index + 1,
        }));
    const threadManifest = threadManifestRaw;
    const topicDocuments = this.mapKnowledgeBaseItemsToTopicSources(topicKnowledgeItems);
    const sourceOfTruthDocuments = this.mapKnowledgeBaseItemsToTopicSources(sourceOfTruthItems);
    const sourceOfTruthManifest: SourceOfTruthManifestEntry[] = sourceOfTruthItems.map((item, index) => ({
      id: asString(item.id),
      title: item.title || "Untitled",
      index: index + 1,
    }));
    const attachedDocuments = this.mapKnowledgeBaseItemsToTopicSources(attachedItems);

    const agent = agentId ? await this.getAgent(agentId) : null;
    const superPrompt = superPromptId ? this.getSuperPrompt(superPromptId, superPromptVariables) : null;

    const ssotIds = new Set(sourceOfTruthDocuments.map((s) => s.id));
    const attachedIds = new Set(attachedDocuments.map((s) => s.id));
    const spaceAndTopicIdsNew = new Set([...spaceSources, ...topicDocuments].map((s) => s.id));
    const topicMetaSourcesNew = topicId ? this.topicSelfSource(topicId, topicDetails) : [];

    const assembled = this.assembleExplicitSources({
      maxTokens,
      sourceOfTruthDocuments,
      sourceOfTruthManifest,
      topicDocumentManifest,
      layers: [
        topicMetaSourcesNew,
        attachedDocuments.filter((d) => !ssotIds.has(d.id)),
        spaceSources.filter((d) => !ssotIds.has(d.id) && !attachedIds.has(d.id)),
        topicDocuments.filter((d) => !ssotIds.has(d.id) && !attachedIds.has(d.id)),
        spaceKnowledgeDocuments.filter(
          (d) => !ssotIds.has(d.id) && !attachedIds.has(d.id) && !spaceAndTopicIdsNew.has(d.id)
        ),
        agentTrainingSources.filter((d) => !ssotIds.has(d.id) && !attachedIds.has(d.id)),
      ],
    });

    return {
      threadId: null,
      spaceId,
      topicId,
      spaceSources,
      topicDocuments,
      threadAttachments: [],
      linkedDocuments: [],
      sourceOfTruthDocuments,
      sourceOfTruthManifest,
      attachedDocuments,
      userProfile: personalizationEnabled ? userProfile : null,
      personalizationEnabled,
      agent,
      superPrompt,
      workflowContext: null,
      topicDocumentManifest,
      threadManifest,
      allSources: assembled.allSources,
      citationMap: assembled.citationMap,
      metadata: {
        totalSources: assembled.allSources.length,
        totalTokensEstimate: assembled.totalTokensEstimate,
        uncappedTokensEstimate: assembled.uncappedTokensEstimate,
        truncated: assembled.truncated,
        topicDocumentManifestCount: topicDocumentManifest.length,
        topicDocumentContentIncludedCount: assembled.topicDocumentContentIncludedCount,
        threadManifestCount: threadManifest.length,
        sourceOfTruthManifestCount: sourceOfTruthManifest.length,
        sourceOfTruthContentIncludedCount: assembled.sourceOfTruthContentIncludedCount,
        spaceName: spaceDetails?.name,
        topicName: topicDetails?.name
      }
    };
  }

  private async getThread(threadId: string): Promise<ChatThread | null> {
    const { data, error } = await supabase
      .from("chat-threads")
      .select("id, userId, title, topic, topicId, tags, messages, linkedSpaceIds, agentId, contextMode, status, createdAt, updatedAt")
      .eq("id", threadId)
      .single();

    if (error || !data) return null;
    return data as ChatThread;
  }

  private async getSpaceSources(spaceId: string): Promise<SourceContext[]> {
    try {
      const sources = await sourcesService.getSources(spaceId);
      return sources.map((source, index) => ({
        id: asString(source.id),
        title: source.title,
        content: source.content || "",
        type: source.type || "document",
        url: source.url,
        citationIndex: index + 1
      }));
    } catch (error) {
      console.error("Error fetching space sources:", error);
      return [];
    }
  }

  /** Topic-linked Knowledge Base rows (same source as Topic UI), not mastermind document_entity_links. */
  private mapKnowledgeBaseItemsToTopicSources(items: KnowledgeBaseItem[]): SourceContext[] {
    return items.map((item, index) => ({
      id: asString(item.id),
      title: item.title || "Untitled",
      content: item.content || "",
      type: item.type || "document",
      url: item.videoUrl ?? null,
      citationIndex: index + 1
    }));
  }

  private async getSourceOfTruthKnowledgeItems(): Promise<KnowledgeBaseItem[]> {
    try {
      const profile = await userProfileService.getUserProfile();
      const prefs = (profile?.preferences as Record<string, unknown> | null) || null;
      const ids = normalizeSourceOfTruthDocIds(prefs?.sourceOfTruthDocIds);
      if (!isSourceOfTruthEnabledFromPrefs(prefs, ids)) return [];
      return this.getKnowledgeItemsByIds(ids);
    } catch (error) {
      console.error("[contextOrchestrator] Error loading Source of Truth documents:", error);
      return [];
    }
  }

  private async getKnowledgeItemsByIds(ids: string[]): Promise<KnowledgeBaseItem[]> {
    const unique = [...new Set(ids.filter((id) => typeof id === "string" && id.trim()))];
    if (!unique.length) return [];
    const items = await Promise.all(
      unique.map(async (id) => {
        try {
          return await knowledgeBaseService.getKnowledgeBaseItem(id);
        } catch (e) {
          console.warn(`[contextOrchestrator] Failed to load knowledge doc ${id}:`, e);
          return null;
        }
      })
    );
    return items.filter((item): item is KnowledgeBaseItem => item != null);
  }

  private applyTokenBudgetToSources(
    sources: SourceContext[],
    maxTokens: number
  ): { sources: SourceContext[]; totalTokens: number; truncated: boolean } {
    const working = sources.map((s) => ({ ...s, content: s.content }));
    let totalTokens = 0;
    let truncated = false;
    let truncationIndex = working.length;

    for (let i = 0; i < working.length; i++) {
      const source = working[i];
      const sourceTokens = estimateTokens(source.content);
      if (totalTokens + sourceTokens > maxTokens) {
        const remainingTokens = maxTokens - totalTokens;
        if (remainingTokens > 100) {
          source.content = truncateContent(source.content, remainingTokens);
          totalTokens = maxTokens;
          truncationIndex = i + 1;
        } else {
          truncationIndex = i;
        }
        truncated = true;
        break;
      }
      totalTokens += sourceTokens;
    }

    return {
      sources: working.slice(0, truncationIndex),
      totalTokens,
      truncated,
    };
  }

  private assembleExplicitSources(input: {
    maxTokens: number;
    sourceOfTruthDocuments: SourceContext[];
    sourceOfTruthManifest: SourceOfTruthManifestEntry[];
    topicDocumentManifest: TopicDocumentManifestEntry[];
    layers: SourceContext[][];
  }): AssembledSourcesResult {
    const ssotBudget = ssotTokenBudget(input.maxTokens);
    const ssotResult = this.applyTokenBudgetToSources(input.sourceOfTruthDocuments, ssotBudget);
    const otherBudget = Math.max(0, input.maxTokens - ssotResult.totalTokens);

    const otherSources: SourceContext[] = [];
    for (const layer of input.layers) {
      for (const source of layer) {
        otherSources.push({ ...source });
      }
    }
    const otherResult = this.applyTokenBudgetToSources(otherSources, otherBudget);
    const uncappedTokensEstimate =
      input.sourceOfTruthDocuments.reduce((n, s) => n + estimateTokens(s.content), 0) +
      otherSources.reduce((n, s) => n + estimateTokens(s.content), 0);

    let citationIndex = 1;
    const allSources: SourceContext[] = [];
    const citationMap = new Map<number, SourceContext>();

    for (const source of [...ssotResult.sources, ...otherResult.sources]) {
      source.citationIndex = citationIndex;
      allSources.push(source);
      citationMap.set(citationIndex, source);
      citationIndex++;
    }

    const ssotIds = new Set(input.sourceOfTruthManifest.map((e) => e.id));
    const topicIds = new Set(input.topicDocumentManifest.map((e) => e.id));

    return {
      allSources,
      citationMap,
      totalTokensEstimate: ssotResult.totalTokens + otherResult.totalTokens,
      uncappedTokensEstimate,
      truncated: ssotResult.truncated || otherResult.truncated,
      topicDocumentContentIncludedCount: allSources.filter((s) => topicIds.has(s.id)).length,
      sourceOfTruthContentIncludedCount: allSources.filter((s) => ssotIds.has(s.id)).length,
    };
  }

  /** Documents in the knowledge base for this space (and optional topic), e.g. created in Docs tab. */
  private async getSpaceKnowledgeDocuments(spaceId: string, topicId?: string | null): Promise<SourceContext[]> {
    try {
      const topicIds = topicId ? [topicId] : [];
      const items = await knowledgeBaseService.getKnowledgeBaseItemsForSpace(spaceId, topicIds);
      return items
        .filter((item) => item.content)
        .map((item, index) => ({
          id: asString(item.id),
          title: item.title || "Untitled",
          content: item.content || "",
          type: item.type || "document",
          url: null,
          citationIndex: index + 1
        }));
    } catch (error) {
      console.error("Error fetching space knowledge documents:", error);
      return [];
    }
  }

  private async getThreadAttachments(threadId: string): Promise<SourceContext[]> {
    try {
      const linkedSources = await sourcesService.getSourcesByThreadId(threadId);

      return linkedSources.map((source, index) => ({
        id: asString(source.id),
        title: source.title,
        content: source.content || "",
        type: source.type || "document",
        url: source.url,
        citationIndex: index + 1,
      }));
    } catch (error) {
      console.error("Error fetching thread attachments:", error);
      return [];
    }
  }

  /**
   * DS EDIT 7 — explicit thread↔document links (document_entity_links,
   * entity_type 'thread', documents = mastermind_documents). Previously a
   * placeholder returning [], so explicit links never reached chat context.
   * Only EXPLICIT links count — sharing a topic with a document does NOT
   * create a thread-document relationship (Rule 3, no implicit links).
   */
  private async getLinkedDocuments(threadId: string): Promise<SourceContext[]> {
    try {
      const { documentLinkingService } = await import("./documentLinkingService");
      const docs = await documentLinkingService.getLinkedDocumentsForEntity("thread", threadId);
      return docs.map((doc, index) => ({
        id: asString(doc.id),
        title: doc.title || "Untitled",
        content: doc.content || "",
        type: doc.type || "document",
        url: doc.url ?? null,
        citationIndex: index + 1,
      }));
    } catch (error) {
      console.error("[contextOrchestrator] Error fetching linked documents:", error);
      return [];
    }
  }

  private async getUserProfile(): Promise<UserProfileContext | null> {
    try {
      const profile = await userProfileService.getUserProfile();
      if (!profile) return null;

      const profileAny = profile as any;
      const responsePrefs = profileAny.response_preferences || profileAny.responsePreferences || null;

      return {
        writingStyle: profile.writingStyle,
        expertise: profile.expertise || [],
        communicationPreferences: profile.communicationPreferences,
        worldview: profile.worldview,
        goals: profile.goals || [],
        background: profile.background,
        resumeText:
          typeof profileAny.resume_text === "string" && profileAny.resume_text.trim()
            ? profileAny.resume_text.trim()
            : null,
        tone: profileAny.tone,
        verbosity: profileAny.verbosity,
        learningStyle: profileAny.learningStyle,
        responseFormat: profileAny.responseFormat,
        favoritePhrases: profileAny.favoritePhrases || [],
        avoidPhrases: profileAny.avoidPhrases || [],
        referenceAuthors: profileAny.referenceAuthors || [],
        responsePreferences: responsePrefs
      };
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  }

  private async getSpaceDetails(spaceId: string): Promise<{ name: string } | null> {
    try {
      const { data, error } = await supabase
        .from("mastermind")
        .select("name")
        .eq("id", spaceId)
        .single();
      
      if (error || !data) return null;
      return { name: data.name };
    } catch (error) {
      return null;
    }
  }

  /** Title-only inventory of threads linked to the active Knowledge scope. */
  private async getThreadManifestForScope(
    spaceId: string | null,
    topicId: string | null
  ): Promise<ThreadManifestEntry[]> {
    if (!spaceId && !topicId) return [];
    try {
      const summaries = topicId
        ? await chatThreadsService.getThreadSummariesByTopic(topicId)
        : spaceId
          ? await chatThreadsService.getThreadSummariesBySpace(spaceId)
          : [];
      return summaries
        .filter((t) => t.includeInRag !== false)
        .map((t, index) => ({
          id: asString(t.id),
          title: t.title || "Untitled conversation",
          index: index + 1,
        }));
    } catch (error) {
      console.warn("[contextOrchestrator] getThreadManifestForScope failed:", error);
      return [];
    }
  }

  private async getTopicDetails(
    topicId: string
  ): Promise<{ name: string; description: string | null } | null> {
    try {
      const { data, error } = await supabase
        .from("topics")
        .select("name, description")
        .eq("id", topicId)
        .single();
      
      if (error || !data) return null;
      return {
        name: data.name,
        description: typeof data.description === "string" ? data.description : null,
      };
    } catch (error) {
      return null;
    }
  }

  /** Explicit source so the model sees the topic itself when chatting in topic scope. */
  private topicSelfSource(
    topicId: string,
    topicDetails: { name: string; description: string | null } | null
  ): SourceContext[] {
    if (!topicDetails) return [];
    const body = [topicDetails.name, topicDetails.description?.trim()]
      .filter(Boolean)
      .join("\n\n");
    return [
      {
        id: `topic_meta_${topicId}`,
        title: `Topic: ${topicDetails.name}`,
        content: body || topicDetails.name,
        type: "topic",
        url: null,
        citationIndex: 0,
      },
    ];
  }

  private normalizeDbPersona(raw: unknown): AgentContext["persona"] {
    if (typeof raw === "string") {
      return raw;
    }
    if (raw && typeof raw === "object") {
      const p = raw as Record<string, unknown>;
      return {
        role: typeof p.role === "string" ? p.role : undefined,
        expertise: Array.isArray(p.expertise) ? (p.expertise as string[]) : [],
        communicationStyle: typeof p.communicationStyle === "string" ? p.communicationStyle : undefined,
        systemPrompt: typeof p.systemPrompt === "string" ? p.systemPrompt : "",
      };
    }
    return "";
  }

  private normalizeOutputRequirements(raw: unknown): AgentContext["outputRequirements"] | undefined {
    if (!raw || typeof raw !== "object") return undefined;
    const o = raw as Record<string, unknown>;
    return {
      format: typeof o.format === "string" ? o.format : undefined,
      instructions: typeof o.instructions === "string" ? o.instructions : undefined,
      requiredSections: Array.isArray(o.requiredSections)
        ? (o.requiredSections as string[]).filter((s) => typeof s === "string")
        : undefined,
      maxLengthHint: typeof o.maxLengthHint === "string" ? o.maxLengthHint : undefined,
    };
  }

  private async getAgentTrainingSources(agentId: string): Promise<SourceContext[]> {
    try {
      if (getBuiltinAgentById(agentId) || !isWorkflowUuid(agentId)) return [];

      const userId = await getCurrentUserId();
      if (!userId) return [];

      const { data, error } = await supabase
        .from("ai_agents")
        .select("training_document_ids, linked_source_ids")
        .eq("id", agentId)
        .eq("user_id", userId)
        .maybeSingle();

      if (error || !data) return [];

      const row = data as Record<string, unknown>;
      const trainingIds = (row.training_document_ids ?? row.trainingDocumentIds) as string[] | undefined;
      const sourceIds = (row.linked_source_ids ?? row.linkedSourceIds) as string[] | undefined;

      const kbItems = trainingIds?.length
        ? await this.getKnowledgeItemsByIds(trainingIds)
        : [];
      const kbSources = this.mapKnowledgeBaseItemsToTopicSources(kbItems);

      const linkedSources: SourceContext[] = [];
      if (sourceIds?.length) {
        for (const id of sourceIds) {
          try {
            const source = await sourcesService.getSource(id);
            if (source) {
              linkedSources.push({
                id: asString(source.id),
                title: source.title,
                content: source.content || "",
                type: source.type || "document",
                url: source.url,
                citationIndex: 0,
              });
            }
          } catch {
            // skip missing sources
          }
        }
      }

      return [...kbSources, ...linkedSources].map((s, index) => ({
        ...s,
        citationIndex: index + 1,
      }));
    } catch (error) {
      console.error("[contextOrchestrator] Error loading agent training sources:", error);
      return [];
    }
  }

  private async getAgent(agentId: string): Promise<AgentContext | null> {
    // First check built-in agents
    const builtInAgent = AGENTS.find(a => a.id === agentId);
    if (builtInAgent) {
      return {
        id: builtInAgent.id,
        name: builtInAgent.name,
        persona: builtInAgent.persona,
        capabilityModules: builtInAgent.capabilityModules,
        memoryPolicy: builtInAgent.memoryPolicy,
        preferredModel: builtInAgent.preferredModel
      };
    }

    // If not found, fetch from database using the actual schema (persona is text)
    try {
      const userId = await getCurrentUserId();
      if (!userId) return null;

      const { data, error } = await supabase
        .from("ai_agents")
        .select("*")
        .eq("id", agentId)
        .eq("user_id", userId)
        .single();

      if (error || !data) {
        console.error("Error fetching agent from database:", error);
        return null;
      }

      const row = data as Record<string, unknown>;
      const preferredRaw = row.preferred_model ?? row.preferredModel;
      const preferredModel =
        typeof preferredRaw === "string" && preferredRaw.trim() ? preferredRaw.trim() : undefined;

      const memoryRaw = row.memory_policy ?? row.memoryPolicy;
      let memoryPolicy: AgentContext["memoryPolicy"] = { type: "session" };
      if (memoryRaw && typeof memoryRaw === "object") {
        const m = memoryRaw as Record<string, unknown>;
        const t = m.type;
        if (t === "none" || t === "session" || t === "persistent") {
          memoryPolicy = {
            type: t,
            retentionDays: typeof m.retentionDays === "number" ? m.retentionDays : undefined,
          };
        }
      }

      const capabilityRaw = row.capability_modules ?? row.capabilityModules;
      const capabilityModules = Array.isArray(capabilityRaw)
        ? (capabilityRaw as string[])
        : [];

      return {
        id: data.id as string,
        name: data.name as string,
        description: (data.description as string) || undefined,
        specialty: (row.specialty as string) || undefined,
        lens: (row.lens as string) || undefined,
        expertFocus: (row.expert_focus ?? row.expertFocus) as string | undefined,
        outputRequirements: this.normalizeOutputRequirements(
          row.output_requirements ?? row.outputRequirements
        ),
        persona: this.normalizeDbPersona(row.persona),
        capabilityModules,
        memoryPolicy,
        preferredModel,
      };
    } catch (error) {
      console.error("Error fetching agent from database:", error);
      return null;
    }
  }

  private getSuperPrompt(promptId: string, variables: Record<string, string>): SuperPromptContext | null {
    const prompt = QUICK_PROMPTS.find(p => p.id === promptId);
    if (!prompt) return null;

    let filledTemplate = prompt.promptTemplate;
    for (const [key, value] of Object.entries(variables)) {
      filledTemplate = filledTemplate.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    }

    return {
      id: prompt.id,
      name: prompt.name,
      promptTemplate: filledTemplate,
      filledVariables: variables
    };
  }

  private buildSourcePartStrings(allSources: SourceContext[]): string[] {
    return allSources.map(
      (source) => `### [${source.citationIndex}] ${source.title}
Type: ${source.type}
${source.url ? `URL: ${source.url}` : ""}

${source.content}`
    );
  }

  buildSystemPrompt(context: ContextPayload): string {
    const sections: string[] = [];

    if (context.agent) {
      // Build comprehensive agent persona section
      const agentParts: string[] = [];
      
      agentParts.push(`You are ${context.agent.name}.`);
      
      if (context.agent.description) {
        agentParts.push(`Description: ${context.agent.description}`);
      }
      
      if (context.agent.specialty) {
        agentParts.push(`Specialty: ${context.agent.specialty}`);
      }
      
      if (context.agent.lens) {
        const lensDescriptions: Record<string, string> = {
          analytical: "Analytical - Focused on data and facts",
          creative: "Creative - Imaginative and innovative",
          critical: "Critical - Questioning and evaluative",
          practical: "Practical - Solution-oriented",
          empathetic: "Empathetic - Understanding and supportive",
          strategic: "Strategic - Long-term planning"
        };
        agentParts.push(`Perspective: ${lensDescriptions[context.agent.lens] || context.agent.lens}`);
      }

      if (context.agent.expertFocus) {
        agentParts.push(`Expert Focus: ${context.agent.expertFocus}`);
      }
      
      // Handle persona - can be object (built-in) or string (database)
      let systemPrompt = "";
      if (typeof context.agent.persona === "string") {
        // Database agent - persona is the system prompt text
        systemPrompt = context.agent.persona;
      } else {
        // Built-in agent - persona is an object
        if (context.agent.persona.role) {
          agentParts.push(`Role: ${context.agent.persona.role}`);
        }
        if (context.agent.persona.expertise && context.agent.persona.expertise.length > 0) {
          agentParts.push(`Expertise: ${context.agent.persona.expertise.join(", ")}`);
        }
        if (context.agent.persona.communicationStyle) {
          agentParts.push(`Communication Style: ${context.agent.persona.communicationStyle}`);
        }
        systemPrompt = context.agent.persona.systemPrompt || "";
      }
      
      sections.push(`## AGENT IDENTITY & INSTRUCTIONS
${agentParts.join("\n")}

${systemPrompt ? `\n## AGENT SYSTEM PROMPT\n${systemPrompt}` : ""}

**IMPORTANT**: You must act strictly as ${context.agent.name}. Follow all instructions above and respond in character as this agent. Your responses should reflect the agent's personality, expertise, and communication style.`);

      const outputReq = context.agent.outputRequirements;
      if (
        outputReq &&
        (outputReq.instructions ||
          outputReq.format ||
          outputReq.maxLengthHint ||
          (outputReq.requiredSections && outputReq.requiredSections.length > 0))
      ) {
        const outputParts: string[] = [];
        if (outputReq.format) outputParts.push(`Format: ${outputReq.format}`);
        if (outputReq.maxLengthHint) outputParts.push(`Length: ${outputReq.maxLengthHint}`);
        if (outputReq.instructions) outputParts.push(`Instructions:\n${outputReq.instructions}`);
        if (outputReq.requiredSections?.length) {
          outputParts.push(
            `Required sections (include all):\n${outputReq.requiredSections.map((s) => `- ${s}`).join("\n")}`
          );
        }
        sections.push(`## OUTPUT REQUIREMENTS\n${outputParts.join("\n\n")}`);
      }
    }

    if (context.personalizationEnabled && context.userProfile) {
      const profileParts: string[] = [];
      if (context.userProfile.background) profileParts.push(`Background: ${context.userProfile.background}`);
      if (context.userProfile.expertise?.length) profileParts.push(`Expertise: ${context.userProfile.expertise.join(", ")}`);
      if (context.userProfile.goals?.length) profileParts.push(`Goals: ${context.userProfile.goals.join(", ")}`);
      if (context.userProfile.writingStyle) profileParts.push(`Preferred Writing Style: ${context.userProfile.writingStyle}`);
      if (context.userProfile.tone) profileParts.push(`Tone Preference: ${context.userProfile.tone}`);
      if (context.userProfile.verbosity) profileParts.push(`Verbosity Preference: ${context.userProfile.verbosity}`);
      if (context.userProfile.learningStyle) profileParts.push(`Learning Style: ${context.userProfile.learningStyle}`);
      if (context.userProfile.worldview) profileParts.push(`Worldview: ${context.userProfile.worldview}`);
      if (context.userProfile.communicationPreferences) profileParts.push(`Communication Preferences: ${context.userProfile.communicationPreferences}`);
      if (context.userProfile.favoritePhrases?.length) profileParts.push(`Favorite Phrases: ${context.userProfile.favoritePhrases.join(", ")}`);
      if (context.userProfile.avoidPhrases?.length) profileParts.push(`Phrases to Avoid: ${context.userProfile.avoidPhrases.join(", ")}`);
      if (context.userProfile.referenceAuthors?.length) profileParts.push(`Reference Authors: ${context.userProfile.referenceAuthors.join(", ")}`);
      if (context.userProfile.resumeText?.trim()) {
        profileParts.push(
          `Professional Background & Experience (from Resume/CV):\n${context.userProfile.resumeText.trim()}`
        );
      }

      if (profileParts.length > 0) {
        sections.push(`## USER PROFILE
Personalize your responses based on this user's profile:
${profileParts.join("\n")}`);
      }

      if (context.userProfile.responsePreferences) {
        const responseInstructions = generateResponseInstructions(context.userProfile.responsePreferences);
        if (responseInstructions) {
          sections.push(`## RESPONSE FORMAT INSTRUCTIONS
${responseInstructions}`);
        }
      }
    }

    const ssotIds = new Set(context.sourceOfTruthManifest.map((e) => e.id));
    const ssotInContext = context.allSources.filter((s) => ssotIds.has(s.id));

    if (context.sourceOfTruthManifest.length > 0) {
      const manifestLines = context.sourceOfTruthManifest.map((doc) => `${doc.index}. ${doc.title}`);
      const ssotParts: string[] = [
        `## SINGLE SOURCE OF TRUTH`,
        `These documents are the user's authoritative canon across all conversations. When they apply, prefer them over generic knowledge and resolve conflicts in their favor. Cite using [number] format.`,
      ];
      if (manifestLines.length > 0) {
        ssotParts.push(
          `\nCanon documents (${context.sourceOfTruthManifest.length}):\n${manifestLines.join("\n")}`
        );
      }
      if (ssotInContext.length < context.sourceOfTruthManifest.length) {
        ssotParts.push(
          `\nFull content is included for ${context.metadata.sourceOfTruthContentIncludedCount} of ${context.sourceOfTruthManifest.length} canon document(s) below (others omitted due to context limits).`
        );
      }
      if (ssotInContext.length > 0) {
        ssotParts.push(
          `\n${this.buildSourcePartStrings(ssotInContext).join("\n\n---\n\n")}`
        );
      }
      sections.push(ssotParts.join("\n"));
    }

    if ((context.topicDocumentManifest?.length ?? 0) > 0 || (context.threadManifest?.length ?? 0) > 0) {
      const scopeLabel = context.topicId
        ? "this topic"
        : context.spaceId
          ? "this space"
          : "the current knowledge scope";
      const parts: string[] = [
        `## KNOWLEDGE INVENTORY
Titles of documents and chat threads in scope. Full bodies are loaded automatically into retrieved context when the user's question is about them — use retrieved content; do not invent body text from titles alone.`,
      ];
      if ((context.topicDocumentManifest?.length ?? 0) > 0) {
        const docLines = context.topicDocumentManifest.map((doc) => `${doc.index}. ${doc.title}`);
        parts.push(`### Documents in ${scopeLabel} (${context.topicDocumentManifest.length})
${docLines.join("\n")}`);
      }
      if ((context.threadManifest?.length ?? 0) > 0) {
        const threadLines = context.threadManifest.map((t) => `${t.index}. ${t.title}`);
        parts.push(`### Chat threads in ${scopeLabel} (${context.threadManifest.length})
${threadLines.join("\n")}`);
      }
      parts.push(
        `When retrieved sources include full document or thread content below (or in Retrieved Context), base your answer on that content.`
      );
      sections.push(parts.join("\n\n"));
    }

    const nonSsotSources = context.allSources.filter((s) => !ssotIds.has(s.id));
    if (nonSsotSources.length > 0) {
      const sourceSections = this.buildSourcePartStrings(nonSsotSources);

      sections.push(`## KNOWLEDGE BASE
The following sources are available for reference. When using information from these sources, cite them using [number] format.

${sourceSections.join("\n\n---\n\n")}`);
    }

    if (context.superPrompt) {
      sections.push(`## TASK INSTRUCTIONS
${context.superPrompt.promptTemplate}`);
    }

    if (context.metadata.spaceName || context.metadata.topicName) {
      sections.push(`## CONTEXT
${context.metadata.spaceName ? `Space: ${context.metadata.spaceName}` : ""}
${context.metadata.topicName ? `Topic: ${context.metadata.topicName}` : ""}`);
    }

    if (context.allSources.length > 0) {
      sections.push(`## CITATION INSTRUCTIONS
When referencing information from the knowledge base sources shown with content below, cite using [number] format inline.
Example: "According to the research [1], the key finding was..."
Available sources: ${context.allSources.map(s => `[${s.citationIndex}] ${s.title}`).join(", ")}`);
    }

    return sections.join("\n\n---\n\n");
  }

  /**
   * Same output as buildSystemPrompt, but yields during large KB assembly so Send INP can recover.
   */
  async buildSystemPromptAsync(context: ContextPayload): Promise<string> {
    const { yieldToMain, yieldScheduler } = await import("@/lib/yieldToMain");
    await yieldToMain();
    if (context.allSources.length === 0) {
      return this.buildSystemPrompt(context);
    }
    const ssotIds = new Set(context.sourceOfTruthManifest.map((e) => e.id));
    const sansKb: ContextPayload = {
      ...context,
      allSources: [],
      sourceOfTruthManifest: [],
      sourceOfTruthDocuments: [],
    };
    const prefix = this.buildSystemPrompt(sansKb);

    const ssotSources = context.allSources.filter((s) => ssotIds.has(s.id));
    const nonSsotSources = context.allSources.filter((s) => !ssotIds.has(s.id));

    const extraBlocks: string[] = [];

    if (context.sourceOfTruthManifest.length > 0 || ssotSources.length > 0) {
      const manifestLines = context.sourceOfTruthManifest.map((doc) => `${doc.index}. ${doc.title}`);
      const ssotPartStrs = this.buildSourcePartStrings(ssotSources);
      const ssotAcc: string[] = [];
      for (let i = 0; i < ssotPartStrs.length; i++) {
        ssotAcc.push(ssotPartStrs[i]);
        if (i > 0 && i % 8 === 0) await yieldScheduler();
      }
      const ssotLines = [
        `## SINGLE SOURCE OF TRUTH`,
        `These documents are the user's authoritative canon across all conversations. When they apply, prefer them over generic knowledge and resolve conflicts in their favor. Cite using [number] format.`,
      ];
      if (manifestLines.length > 0) {
        ssotLines.push(
          `\nCanon documents (${context.sourceOfTruthManifest.length}):\n${manifestLines.join("\n")}`
        );
      }
      if (ssotSources.length < context.sourceOfTruthManifest.length) {
        ssotLines.push(
          `\nFull content is included for ${context.metadata.sourceOfTruthContentIncludedCount} of ${context.sourceOfTruthManifest.length} canon document(s) below (others omitted due to context limits).`
        );
      }
      if (ssotAcc.length > 0) {
        ssotLines.push(`\n${ssotAcc.join("\n\n---\n\n")}`);
      }
      extraBlocks.push(ssotLines.join("\n"));
    }

    if (nonSsotSources.length > 0) {
      const partStrs = this.buildSourcePartStrings(nonSsotSources);
      const acc: string[] = [];
      for (let i = 0; i < partStrs.length; i++) {
        acc.push(partStrs[i]);
        if (i > 0 && i % 8 === 0) await yieldScheduler();
      }
      extraBlocks.push(
        `## KNOWLEDGE BASE
The following sources are available for reference. When using information from these sources, cite them using [number] format.

${acc.join("\n\n---\n\n")}`
      );
    }

    const citationBlock = `## CITATION INSTRUCTIONS
When referencing information from the knowledge base sources shown with content below, cite using [number] format inline.
Example: "According to the research [1], the key finding was..."
Available sources: ${context.allSources.map((s) => `[${s.citationIndex}] ${s.title}`).join(", ")}`;
    await yieldScheduler();
    return [prefix, ...extraBlocks, citationBlock]
      .filter((s) => typeof s === "string" && s.trim().length > 0)
      .join("\n\n---\n\n");
  }

  formatCitationsForDisplay(citationMap: Map<number, SourceContext>): Array<{ index: number; title: string; url?: string | null }> {
    const citations: Array<{ index: number; title: string; url?: string | null }> = [];
    
    citationMap.forEach((source, index) => {
      citations.push({
        index,
        title: source.title,
        url: source.url
      });
    });

    return citations.sort((a, b) => a.index - b.index);
  }
}

export const contextOrchestrator = new ContextOrchestratorService();
